import * as React from "react"
import { FormInput } from "./Input"
import { FormSelect } from "./Select"
import Autocomplete from "@mui/material/Autocomplete"
import LocationOnIcon from "@mui/icons-material/LocationOn"
import Grid from "@mui/material/Grid"
import Typography from "@mui/material/Typography"
import { TextField, FormControl } from "@mui/material"
import { Countries } from "./../../utils/Countries"
import { FormContextType, useForm } from "../../contexts/formContext"
import { VAT_REQUIRED_COUNTRIES } from "../../checkout/components/steps/CheckoutShippingAddress"
import theme from "../../material-ui/theme"

function loadScript(src: string, position: HTMLElement | null, id: string) {
  if (!position) {
    return
  }
  const script = document.createElement("script")
  script.setAttribute("async", "true")
  script.setAttribute("id", id)
  script.src = src
  position.appendChild(script)
}

const autocompleteService = { current: null }

interface PlaceType {
  structured_formatting: {
    secondary_text: string
    main_text_matched_substrings: [
      {
        offset: number
        length: number
      }
    ]
  }
}

interface AddressProps {
  type: "shipping" | "billing"
  includeCardHolderName?: boolean
  onChange?: () => {}
}

export const Address: React.FC<AddressProps> = ({
  type,
  includeCardHolderName,
  onChange,
}) => {
  // const [inputValue, setInputValue] = React.useState("")
  const [options, setOptions] = React.useState<PlaceType[]>([])
  let loaded = false

  const { formState, formActions } = useForm() as FormContextType

  const [open, setOpen] = React.useState(false)
  const closePopper = () => setOpen(false)
  const openPopper = () => setOpen(true)

  React.useEffect(() => {
    if (typeof window !== "undefined" && !loaded) {
      if (!document.querySelector("#google-maps")) {
        loadScript(
          "https://maps.googleapis.com/maps/api/js?key=AIzaSyCaep8yWTtJSpqWVylvW9ob4EDcDVbPLTM&libraries=places",
          document.querySelector("head"),
          "google-maps"
        )
      }
      loaded = true
    }
  }, [])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // setInputValue(event.target.value)
    let data: any = {}
    data[`${type}Street`] = event.target.value
    formActions.setFormData(data)
    updatePlaces(event.target.value)
    if (onChange) {
      onChange()
    }
  }

  const fetchPlaces = (
    input: { input: string; componentRestrictions?: { country?: string } },
    callback: (results?: PlaceType[]) => void
  ) => {
    ;(autocompleteService.current as any).getPlacePredictions(input, callback)
  }

  const updatePlaces = (inputValue: string) => {
    let active = true

    if (!autocompleteService.current && (window as any).google) {
      autocompleteService.current = new (window as any).google.maps.places.AutocompleteService()
    }
    if (!autocompleteService.current) {
      return undefined
    }

    if (inputValue === "") {
      setOptions([])
      return undefined
    }
    const countryField = formState.inputs[`${type}Country`]
    const country = countryField.value || ""
    const countryCode = Countries.get(country)?.code || ""
    fetchPlaces(
      {
        input: inputValue,
        ...(country && { componentRestrictions: { country: countryCode } }),
      },
      (results?: PlaceType[]) => {
        if (active) {
          setOptions(results || [])
        }
      }
    )
  }

  const getPlacesPostCodeById = async (placeId: string) =>
    new Promise<string>((resolve, reject) => {
      try {
        new (window as any).google.maps.places.PlacesService(
          document.createElement("div")
        ).getDetails(
          {
            placeId,
            fields: ["address_components"],
          },
          (details: {
            address_components: Array<{
              long_name: string
              short_name: string
              types: string[]
            }>
          }) => {
            let postcode = null
            details?.address_components?.forEach(
              (entry: {
                long_name: string
                short_name: string
                types: string[]
              }) => {
                if (entry.types?.[0] === "postal_code") {
                  postcode = entry.long_name
                }
              }
            )
            return resolve(postcode || "")
          }
        )
      } catch (e) {
        reject(e)
      }
    })

  const handleSelectAddress = (option: any) => async () => {
    const postalCode = await getPlacesPostCodeById(option.place_id)

    const parts = option.terms

    const street = `${parts[0].value} ${parts[1].value}`
    const city = parts[2].value
    const country = parts.length >= 5 ? parts[4].value : parts[3].value
    let region = parts.length >= 5 ? parts[3].value : ""

    if (parts.length < 4) {
      region = ""
    }

    if (parts.length >= 4) {
      setTimeout(() => {
        const input = window.document.getElementById(
          "sensai-address"
        ) as HTMLInputElement
        input.value = street
      }, 0)
    }
    let data: any = {}

    data[`${type}City`] = city
    let mappedCountry =
      country === "USA"
        ? "United States"
        : country === "UK"
        ? "United Kingdom"
        : country
    if (Countries.get(mappedCountry)) {
      data[`${type}Country`] = mappedCountry
    } else {
      data[`${type}Country`] = ""
    }
    data[`${type}Postal`] = postalCode
    data[`${type}Province`] = region
    data[`${type}Street`] = street
    formActions.setFormData(data)

    const input = window.document.getElementById(
      "sensai-address"
    ) as HTMLInputElement
    if (input) {
      input.value = street || ""
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (window && e.key === "Enter") {
      const input = window.document.getElementById(
        "sensai-address"
      ) as HTMLInputElement
      if (input.value.split(",").length == 4) {
        handleSelectAddress({
          description: input.value,
        })()
      }
    }
  }

  const handleOnFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    if (event.target.autocomplete) {
      event.target.autocomplete = "disable"
    }
  }

  const handleOnBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    setTimeout(() => {
      const input = window.document.getElementById(
        "sensai-address"
      ) as HTMLInputElement
      if (input) {
        input.value = formState.inputs[`${type}Street`]?.value || ""
      }
    }, 1000)
  }
  const countryList = Array.from(Countries.keys()).sort((a, b) => {
    var nameA = a.toUpperCase() // ignore upper and lowercase
    var nameB = b.toUpperCase() // ignore upper and lowercase
    if (nameA < nameB) {
      return -1
    }
    if (nameA > nameB) {
      return 1
    }

    // names must be equal
    return 0
  })

  return (
    <>
      {type === "shipping" && (
        <Grid item xs={12}>
          <FormControl variant="standard" fullWidth>
            <FormInput
              fieldId="address-first-name"
              fieldName={`${type}FirstName`}
              inputs={formState.inputs}
              inputHandler={formActions.inputHandler}
              autoComplete="off"
            />
          </FormControl>
        </Grid>
      )}
      {type === "shipping" && (
        <Grid item xs={12}>
          <FormControl variant="standard" fullWidth>
            <FormInput
              fieldId="address-last-name"
              fieldName={`${type}LastName`}
              inputs={formState.inputs}
              inputHandler={formActions.inputHandler}
              autoComplete="off"
            />
          </FormControl>
        </Grid>
      )}
      {type === "billing" && includeCardHolderName && (
        <Grid item xs={12}>
          <FormControl variant="standard" fullWidth>
            <FormInput
              fieldId="payment-cardholderName"
              fieldName="cardHolderName"
              inputs={formState.inputs}
              inputHandler={formActions.inputHandler}
            />
          </FormControl>
        </Grid>
      )}
      <Grid item xs={12}>
        <FormControl variant="standard" fullWidth>
          <FormSelect
            fieldId="country-select"
            fieldName={`${type}Country`}
            inputs={formState.inputs}
            inputHandler={formActions.inputHandler}
            values={countryList}
            autoComplete="off"
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <Autocomplete
          id="sensai-address"
          open={open}
          onOpen={openPopper}
          onClose={closePopper}
          style={{ width: "100%", backgroundColor: "white" }}
          getOptionLabel={(option: any) =>
            typeof option === "string" ? option : option.description
          }
          filterOptions={(x: PlaceType[]) => x}
          options={options}
          autoComplete
          includeInputInList
          filterSelectedOptions
          inputValue={formState.inputs[`${type}Street`].value}
          renderInput={(params: any) => {
            const formField = formState.inputs[`${type}Street`]
            return (
              <TextField
                onFocus={handleOnFocus}
                onBlur={handleOnBlur}
                autoComplete="off"
                {...params}
                label="Street Address"
                required={true}
                variant="outlined"
                fullWidth
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                error={!!formField.error}
                helperText={formField.error}
              />
            )
          }}
          renderOption={(x: any, option: any) => {
            // const matches =
            //   option.structured_formatting.main_text_matched_substrings
            return (
              <Grid
                container
                alignItems="center"
                onClick={async () => {
                  await handleSelectAddress(option)()
                  closePopper()
                }}
              >
                <Grid item>
                  <LocationOnIcon
                    sx={{
                      color: theme.palette.text.secondary,
                      marginRight: theme.spacing(2),
                    }}
                  />
                </Grid>
                <Grid item xs>
                  {option.structured_formatting.main_text}
                  <Typography variant="body2" color="textSecondary">
                    {option.structured_formatting.secondary_text}
                  </Typography>
                </Grid>
              </Grid>
            )
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <FormControl variant="standard" fullWidth>
          <FormInput
            fieldId="address-unit"
            fieldName={`${type}Unit`}
            inputs={formState.inputs}
            inputHandler={formActions.inputHandler}
            autoComplete="off"
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <FormControl variant="standard" fullWidth>
          <FormInput
            fieldId="address-city"
            fieldName={`${type}City`}
            inputs={formState.inputs}
            inputHandler={formActions.inputHandler}
            autoComplete="off"
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <FormControl variant="standard" fullWidth>
          <FormInput
            fieldId="address-province"
            fieldName={`${type}Province`}
            inputs={formState.inputs}
            inputHandler={formActions.inputHandler}
            autoComplete="off"
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <FormControl variant="standard" fullWidth>
          <FormInput
            fieldId="address-postal"
            fieldName={`${type}Postal`}
            inputs={formState.inputs}
            inputHandler={formActions.inputHandler}
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <FormControl variant="standard" fullWidth>
          <FormInput
            fieldId="address-phone"
            fieldName={`${type}Phone`}
            inputs={formState.inputs}
            inputHandler={formActions.inputHandler}
            autoComplete="off"
          />
        </FormControl>
      </Grid>
      {type === "shipping" &&
        VAT_REQUIRED_COUNTRIES.includes(
          formState.inputs.shippingCountry?.value
        ) && (
          <Grid item xs={12}>
            <FormControl variant="standard" fullWidth>
              <FormInput
                fieldId="address-vat"
                fieldName={`${type}Vat`}
                inputs={formState.inputs}
                inputHandler={formActions.inputHandler}
                autoComplete="off"
              />
            </FormControl>
          </Grid>
        )}
    </>
  )
}

// TODO:
// -CONSOLE WARNING:
// - Google Maps JavaScript API has been loaded directly without a callback. This is not supported and can lead to race conditions and suboptimal performance. For supported loading patterns please see https://goo.gle/js-api-loading
